'use client';

import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  FormHelperText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { AddIcon, ChevronRightIcon, RemoveIcon } from '@/shared/ui/display/EmojiIcon';

export interface EnumOptionBlockProps {
  attrIndex: number;
  values: string[];
  enumDisplayNames?: Record<string, string>;
  onUpdateEntry: (
    attrIndex: number,
    entryIndex: number,
    field: 'value' | 'displayName',
    value: string
  ) => void;
  onAddEntry: (attrIndex: number) => void;
  onRemoveEntry: (attrIndex: number, entryIndex: number) => void;
}

export function EnumOptionBlock({
  attrIndex,
  values,
  enumDisplayNames,
  onUpdateEntry,
  onAddEntry,
  onRemoveEntry,
}: EnumOptionBlockProps) {
  const nonEmptyCount = useMemo(() => values.filter((v) => v.trim()).length, [values]);
  const hasDuplicateValues = useMemo(() => {
    const seen = new Set<string>();
    for (const v of values) {
      const t = v.trim();
      if (!t) continue;
      if (seen.has(t)) return true;
      seen.add(t);
    }
    return false;
  }, [values]);

  const [expanded, setExpanded] = useState(nonEmptyCount === 0);

  const summaryLabel =
    nonEmptyCount === 0
      ? '옵션 입력 (펼침)'
      : `옵션 ${nonEmptyCount}개${expanded ? '' : ' · 펼치기'}`;

  return (
    <Box sx={{ pl: 1, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
      <Button
        size="small"
        variant="text"
        onClick={() => setExpanded((e) => !e)}
        sx={{
          alignSelf: 'flex-start',
          minHeight: 32,
          py: 0.25,
          px: 0.5,
          color: 'text.secondary',
          '& .MuiButton-startIcon': {
            transition: 'transform 150ms ease',
            transform: expanded ? 'rotate(90deg)' : 'none',
          },
        }}
        startIcon={<ChevronRightIcon fontSize="small" />}
      >
        {summaryLabel}
      </Button>

      <Collapse in={expanded}>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Table size="small" padding="none" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 100, fontWeight: 600 }}>value</TableCell>
                <TableCell sx={{ minWidth: 88, fontWeight: 600 }}>표시이름</TableCell>
                <TableCell align="right" sx={{ width: 44, p: 0.5 }} aria-label="행 삭제" />
              </TableRow>
            </TableHead>
            <TableBody>
              {values.map((v, entryIndex) => {
                const isEmpty = !v.trim();
                const isValueDuplicate =
                  !isEmpty &&
                  values.filter((val, i) => i !== entryIndex && val.trim() === v).length > 0;
                const canEditDisplayName = !isEmpty;

                return (
                  <TableRow key={entryIndex}>
                    <TableCell>
                      <TextField
                        size="small"
                        placeholder="value"
                        value={v}
                        fullWidth
                        variant="standard"
                        error={isValueDuplicate}
                        onChange={(e) => {
                          const newVal = e.target.value;
                          const otherEmpties = values.filter(
                            (val, i) => i !== entryIndex && !val.trim()
                          );
                          if (!newVal.trim() && otherEmpties.length > 0) return;
                          const otherSame = values.filter(
                            (val, i) => i !== entryIndex && val === newVal
                          );
                          if (newVal.trim() && otherSame.length > 0) return;
                          onUpdateEntry(attrIndex, entryIndex, 'value', newVal);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        placeholder="표시이름"
                        value={enumDisplayNames?.[v] ?? ''}
                        fullWidth
                        variant="standard"
                        disabled={!canEditDisplayName}
                        onChange={(e) =>
                          canEditDisplayName &&
                          onUpdateEntry(attrIndex, entryIndex, 'displayName', e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                      <IconButton
                        size="small"
                        onClick={() => onRemoveEntry(attrIndex, entryIndex)}
                        aria-label="옵션 값 삭제"
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {hasDuplicateValues && (
          <FormHelperText error sx={{ mx: 0.5 }}>
            일부 value가 중복되었습니다. 중복 행을 수정해 주세요.
          </FormHelperText>
        )}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAddEntry(attrIndex)}
          disabled={values.some((val) => !val.trim())}
          sx={{ alignSelf: 'flex-start', mt: 0.5 }}
        >
          옵션 값 추가
        </Button>
      </Collapse>
    </Box>
  );
}
